import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getShiftSwapHistory } from '../api/Attendance/shiftSwap';
import { ShiftSwapRequestView, ShiftSwapHistoryQuery } from '../models/shiftSwap';

const DEFAULT_PAGE_SIZE = 20;

interface UseShiftSwapHistoryResult {
  items: ShiftSwapRequestView[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  query: ShiftSwapHistoryQuery;
  setQuery: (patch: Partial<ShiftSwapHistoryQuery>) => void;
  refresh: () => Promise<void>;
}

/**
 * Server-paged manager/HR team swap history. `enabled` gates fetching so the
 * request only fires when the Team-history tab is open (and the caller is an
 * approver). Any filter change resets to page 1; changing `page` does not.
 */
export function useShiftSwapHistory(enabled: boolean): UseShiftSwapHistoryResult {
  const { t } = useTranslation();
  const [query, setQueryState] = useState<ShiftSwapHistoryQuery>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [items, setItems] = useState<ShiftSwapRequestView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id: keystroke-fast filtering fires overlapping requests, so only the most
  // recent one is allowed to write state — otherwise a slower earlier response clobbers fresh results.
  const reqIdRef = useRef(0);

  // Shared fetch body. `silent` skips the loading toggle so filter/search/page changes
  // revalidate in place instead of flashing the whole list back to the skeleton.
  const load = useCallback(async (silent: boolean) => {
    if (!enabled) return;
    const reqId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await getShiftSwapHistory(query);
      if (reqIdRef.current !== reqId) return; // a newer request superseded this one
      if (res.errors.length === 0 && res.payload) {
        setItems(res.payload.items);
        setTotal(res.payload.totalCount);
      } else {
        setError(res.errors[0]?.description || t('Could not load swap history'));
      }
    } catch {
      if (reqIdRef.current !== reqId) return;
      setError(t('Could not load swap history'));
    }
    if (reqIdRef.current === reqId && !silent) setLoading(false);
  }, [enabled, query, t]);

  // Foreground refresh (shows the skeleton) — used by the error-state retry button.
  const refresh = useCallback(() => load(false), [load]);

  // First fetch after the tab opens shows the skeleton; subsequent query changes
  // (search / filter / page) revalidate silently so content never flashes.
  const primedRef = useRef(false);
  useEffect(() => {
    if (!enabled) {
      primedRef.current = false;
      return;
    }
    load(primedRef.current);
    primedRef.current = true;
  }, [enabled, load]);

  const setQuery = useCallback((patch: Partial<ShiftSwapHistoryQuery>) => {
    setQueryState((prev) => {
      const next = { ...prev, ...patch };
      if (!('page' in patch)) next.page = 1;
      return next;
    });
  }, []);

  return {
    items,
    total,
    page: query.page ?? 1,
    pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    loading,
    error,
    query,
    setQuery,
    refresh,
  };
}

export default useShiftSwapHistory;
